import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, X } from '@phosphor-icons/react';
import api from '../lib/api';
import { toast } from 'sonner';

/**
 * Banner that appears at the top of the tenant Layout when a super-admin is
 * currently impersonating ("Login as company") a tenant. Allows quick exit
 * back to the SaaS console.
 */
export default function ImpersonationBanner() {
  const [state, setState] = useState(null);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const r = await api.get('/admin/impersonation-state');
      setState(r.data || null);
    } catch { setState(null); }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const handler = () => load();
    window.addEventListener('impersonation-changed', handler);
    window.addEventListener('focus', handler);
    return () => {
      window.removeEventListener('impersonation-changed', handler);
      window.removeEventListener('focus', handler);
    };
  }, [load]);

  const exit = async () => {
    try {
      await api.post('/admin/exit-impersonate');
      toast.success('Impersonation ended');
      navigate('/super-admin');
    } catch { toast.error('Exit failed'); }
  };

  if (!state?.impersonating) return null;

  return (
    <div data-testid="tenant-impersonation-banner"
      className="bg-amber-500 text-stone-900 px-4 sm:px-6 py-2 flex items-center gap-3 sticky top-0 z-40 shadow">
      <Eye size={18} weight="fill" />
      <p className="text-xs sm:text-sm flex-1 font-semibold">
        Super-admin viewing <span className="font-bold underline">{state.company_name}</span> — full edit mode
      </p>
      <button onClick={() => navigate('/super-admin')}
        data-testid="back-to-console"
        className="text-xs px-3 py-1 rounded-full bg-stone-900 text-amber-300 hover:bg-stone-800 font-semibold">
        Back to console
      </button>
      <button onClick={exit}
        data-testid="exit-impersonate-tenant"
        title="Exit impersonation"
        className="h-7 w-7 rounded-full bg-white/40 hover:bg-white/60 flex items-center justify-center">
        <X size={14} />
      </button>
    </div>
  );
}
